/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import {
  Box,
  useTheme,
  Grid,
  MenuItem,
  Typography,
  Stack,
  ListSubheader,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import { Alert } from "@/components/Modal";
import { POST_STATUS, POST_TYPE } from "@/@types/EnumType";
import { ICreatePost, IVWPost } from "@/interfaces/post/post";
import { IAsset, ICurrency } from "@/interfaces";
import { useWeb3Modal } from "@web3modal/react";
import { enqueueSnackbar } from "notistack";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import CommonStore from "@/store/common";
import { UserStore } from "@/store/user";
import { useNavigate } from "react-router-dom";
import { IAlertData } from "@/@types/Alert";
import { autorun } from "mobx";
import { formatedBigNumber } from "@/utility";
import MarketContractStore from "@/store/contract";

interface IManagePost {
  initialPostData: IVWPost;
  setSelectedPost: (newValue: IVWPost | null) => void;
  updatePost: (request: {
    updatedPost: ICreatePost;
    user_id: string;
    post_id: string;
  }) => Promise<boolean>;
  removePost: (request: {
    user_id: string;
    post_id: string;
  }) => Promise<boolean>;
  openPost: (request: { post_id: string }) => Promise<boolean>;
  closePost: (request: { post_id: string }) => Promise<boolean>;
  userStore?: UserStore;
  commonStore?: CommonStore;
  assetList: IAsset[] | undefined;
  currencyList: ICurrency[] | undefined;
  user_id: string | undefined;
  isAnyOrderOpen: boolean;
  contractStore?: MarketContractStore;
}

const ManagePost = inject(
  Store.type.userStore,
  Store.type.commonStore,
  Store.type.tradingStore,
  Store.type.contractStore,
)(
  observer(
    ({
      setSelectedPost,
      initialPostData,
      updatePost,
      removePost,
      userStore,
      commonStore,
      assetList,
      openPost,
      closePost,
      currencyList,
      user_id,
      isAnyOrderOpen,
      contractStore,
    }: IManagePost) => {
      const theme = useTheme();
      const navigate = useNavigate();

      const [isSubmiting, setIsSubmiting] = useState(false);
      const [postType, setPostType] = useState<POST_TYPE>(
        initialPostData.post_type,
      );
      const [postStatus, setPostStatus] = useState<POST_STATUS>(
        initialPostData.status,
      );
      const [asset, setAsset] = useState(initialPostData.asset_id ?? "");
      const [merchant, setMerchant] = useState(initialPostData.title);
      const [merchantError, setMerchantError] = useState("");
      const [minAmount, setMinAmount] = useState(
        initialPostData.min_amount.toString(),
      );
      const [maxAmount, setMaxAmount] = useState(
        initialPostData.max_amount.toString(),
      );
      const [price, setPrice] = useState(initialPostData.price.toString());
      const [paymentMethod, setPaymentMethod] = useState("");
      const [currency, setCurrency] = useState(initialPostData.currency);
      const [terms, setTerms] = useState(initialPostData.description);
      const [termsError, setTermsError] = useState("");
      const [alertData, setAlertData] = useState<undefined | IAlertData>();
      const [isNewCryptoAddress, setIsNewCryptoAddress] = useState(false);
      const [postLocked, setPostLocked] = useState(false);
      const [cryptoAddress, setCryptoAddress] = useState<string | undefined>(
        initialPostData.address,
      );
      const [token_amount, setToken_amount] = useState(
        initialPostData.token_amount,
      );
      const { address } = useAccount();
      const { open } = useWeb3Modal();

      const [paymentList, setPaymentList] = useState<
        (false | JSX.Element)[][] | null
      >(null);

      useEffect(() => {
        const disposeAutorun = autorun(() => {
          if (commonStore && userStore) {
            const list = commonStore?.paymentMethods
              .filter((method) =>
                userStore.myPayments?.find(
                  (payment) => payment.payment_method_id === method.payment_id,
                ),
              )
              .map((method) => [
                <ListSubheader
                  key={method.payment_id}
                  sx={{
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: theme.typography.body2.lineHeight,
                    mt: "0.875rem",
                    mb: "0.375rem",
                  }}
                >
                  {method.payment_name}
                </ListSubheader>,
                ...userStore.myPayments
                  .filter(
                    (payment) =>
                      payment.payment_method_id === method.payment_id,
                  )
                  .map((payment) => {
                    const paymentAttributes = payment.payment_attributes
                      .filter(
                        (paymentAttribute) =>
                          paymentAttribute.payment_method_attribute
                            ?.preview_order,
                      )
                      .sort(
                        (a, b) =>
                          (a.payment_method_attribute?.preview_order ?? 0) -
                          (b.payment_method_attribute?.preview_order ?? 0),
                      );
                    const itemText = paymentAttributes
                      .map(
                        (paymentAttribute) => paymentAttribute.attribute_value,
                      )
                      .join(" ");
                    return (
                      <MenuItem key={payment.id} value={payment.id}>
                        {itemText}
                      </MenuItem>
                    );
                  }),
              ]);
            setPaymentList(list.length ? list : null);
            setPaymentMethod(initialPostData.payment_method);
          }
        });
        return () => {
          disposeAutorun();
        };
      }, [commonStore, userStore]);
      useEffect(() => {
        if (contractStore && initialPostData) {
          contractStore
            .getPost(initialPostData.contract_post_id || "")
            .then((post) => {
              if (post) {
                setPostLocked(post.isLocked || false);
              }
            });
        }
      }, [contractStore, initialPostData]);

      const handleUpdatePost = async () => {
        setIsSubmiting(true);
        let isError = false;
        if (!merchant) {
          setMerchantError("Please fill merchant.");
          isError = true;
        } else {
          setMerchantError("");
        }
        if (!terms) {
          setTermsError("Please fill terms.");
          isError = true;
        } else {
          setTermsError("");
        }
        if (isError) {
          setIsSubmiting(false);
          return;
        }

        try {
          if (user_id && cryptoAddress) {
            const updatedPost: ICreatePost = {
              title: merchant,
              post_type: postType,
              asset_id: asset,
              price: price,
              min_amount: minAmount,
              max_amount: maxAmount,
              payment_method: paymentMethod,
              currency,
              status: postStatus,
              description: terms,
              address: cryptoAddress,
              token_amount: token_amount,
            };

            await updatePost({
              updatedPost,
              user_id,
              post_id: initialPostData.post_id,
            });
            setAlertData({
              message: "Successfully update post!",
              closeMethod: () => {
                setAlertData(undefined);
                setSelectedPost(null);
              },
            });
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          setAlertData({
            message: "Update post failed: " + e.message,
            closeMethod: () => {
              setAlertData(undefined);
            },
          });
        } finally {
          setIsSubmiting(false);
        }
      };

      const handleRemovePost = async () => {
        if (isAnyOrderOpen) {
          enqueueSnackbar("Error requesting phone number verification", {
            variant: "error",
          });
          return;
        }
        if (user_id) {
          try {
            await removePost({ user_id, post_id: initialPostData.post_id });
            setSelectedPost(null);
            enqueueSnackbar("Post removed successfully.", {
              variant: "success",
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            console.error("handleRemovePost error:", error);
            enqueueSnackbar(error.message, {
              variant: "error",
            });
          }
        }
      };

      return (
        <Dialog
          open={true}
          sx={{
            "& .MuiPaper-root": {
              px: "0rem",
              pt: "37.87px",
              pb: "56px",
              overflow: "hidden",
              width: "90%",
              maxWidth: "853px",
            },
          }}
        >
          <DialogTitle variant="h3" mb="31.13px" p="0 !important">
            Update post
          </DialogTitle>
          <DialogContent
            sx={{
              px: "3rem !important",
              py: "0 !important",
            }}
          >
            <Grid container columnSpacing="40px" rowSpacing="29px">
              <Grid item sm={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="text"
                  required
                  name="merchant"
                  id="merchant"
                  label="Merchant"
                  value={merchant}
                  error={!!merchantError}
                  helperText={merchantError}
                  onChange={(e) => setMerchant(e.target.value)}
                  sx={{ mb: "29px" }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  select={true}
                  type="text"
                  name="postStatus"
                  id="postStatus"
                  label="Post status"
                  value={postStatus}
                  disabled
                  sx={{
                    mb: "29px",
                    "& .MuiInputBase-input": {
                      color: `${
                        postStatus === POST_STATUS.INACTIVE ||
                        postStatus === POST_STATUS.CANCELLED
                          ? theme.colors.red
                          : theme.colors.green
                      } !important`,
                      WebkitTextFillColor: "initial !important",
                      opacity: "0.6",
                    },
                  }}
                >
                  <MenuItem
                    value={POST_STATUS.ACTIVE}
                    sx={{
                      color: theme.colors.green,
                    }}
                  >
                    OPEN
                  </MenuItem>
                  <MenuItem
                    value={POST_STATUS.INACTIVE}
                    sx={{
                      color: theme.colors.red,
                    }}
                  >
                    CLOSED
                  </MenuItem>
                </TextField>
                <TextField
                  variant="outlined"
                  fullWidth
                  select={true}
                  type="text"
                  name="postType"
                  id="postType"
                  label="Post type"
                  value={postType}
                  disabled
                  sx={{
                    mb: "29px",
                    "& .MuiInputBase-input": {
                      color: `${
                        postType === "SELL"
                          ? theme.colors.red
                          : theme.colors.green
                      } !important`,
                      WebkitTextFillColor: "initial !important",
                      opacity: "0.6",
                    },
                  }}
                >
                  <MenuItem
                    value="SELL"
                    sx={{
                      color: theme.colors.red,
                    }}
                  >
                    SELL POST
                  </MenuItem>
                  <MenuItem
                    value="BUY"
                    sx={{
                      color: theme.colors.green,
                    }}
                  >
                    BUY POST
                  </MenuItem>
                </TextField>
                <TextField
                  variant="outlined"
                  fullWidth
                  select={true}
                  type="text"
                  name="asset"
                  id="asset"
                  label="Asset"
                  value={asset}
                  disabled
                  sx={{ mb: "29px" }}
                >
                  {assetList ? (
                    assetList.map((pm) => (
                      <MenuItem key={pm.asset_id} value={pm.asset_id}>
                        {pm.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={""}>no data</MenuItem>
                  )}
                </TextField>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="number"
                  name="min_amount"
                  id="min_amount"
                  label={`Minimum Amount (${currency})`}
                  value={formatedBigNumber(minAmount)}
                  disabled
                  sx={{ mb: "29px" }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  type="number"
                  name="max_amount"
                  id="max_amount"
                  label={`Maximum Amount (${currency})`}
                  value={formatedBigNumber(maxAmount)}
                  disabled
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="number"
                  name="price"
                  id="price"
                  label="Price / Unit"
                  value={formatedBigNumber(price)}
                  disabled
                  sx={{ mb: "29px" }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  select={true}
                  type="text"
                  name="paymentMethod"
                  id="paymentMethod"
                  label="Payment method"
                  value={paymentMethod}
                  disabled
                  sx={{ mb: "29px" }}
                >
                  {paymentList}
                  {paymentList === null && (
                    <MenuItem
                      onClick={() => {
                        navigate("/profile/payments/create");
                      }}
                    >
                      Add Payment
                    </MenuItem>
                  )}
                </TextField>
                <TextField
                  variant="outlined"
                  fullWidth
                  select={true}
                  type="text"
                  name="currency"
                  label="Currency"
                  id="currency"
                  value={currency}
                  disabled
                  sx={{ mb: "29px" }}
                >
                  {currencyList &&
                    currencyList.map((pm) => (
                      <MenuItem key={pm.id} value={pm.symbol}>
                        {pm.symbol}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="text"
                  required
                  name="terms"
                  id="terms"
                  label="Terms"
                  value={terms}
                  error={!!termsError}
                  helperText={termsError}
                  onChange={(e) => setTerms(e.target.value)}
                  multiline={true}
                  sx={{
                    mb: "29px",
                    "& textarea.MuiInputBase-input.MuiInputBase-inputMultiline":
                      {
                        height: "63px !important",
                      },
                  }}
                />

                <Stack direction="row" gap="1rem">
                  <TextField
                    variant="outlined"
                    fullWidth
                    type="text"
                    name="address"
                    id="address"
                    placeholder="Please Connect your wallet."
                    label="Created by address."
                    value={cryptoAddress ?? ""}
                    disabled
                    sx={{ mb: "29px" }}
                  />
                </Stack>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="number"
                  name="token_amount"
                  id="token_amount"
                  label="Token Amount"
                  value={formatedBigNumber(token_amount)}
                  disabled
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ mt: "47px" }}>
            <Stack
              direction="row"
              justifyContent="center"
              gap="20px"
              width="fit-content"
              mx="auto"
              height="fit-content"
              flexWrap="wrap"
              p="0"
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleUpdatePost()}
                loading={isSubmiting}
                sx={{
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Update
              </Button>
              {initialPostData.status == POST_STATUS.INACTIVE && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    openPost({
                      post_id: initialPostData.contract_post_id || "",
                    });
                    setSelectedPost(null);
                  }}
                >
                  Open Post
                </Button>
              )}
              {initialPostData.status == POST_STATUS.ACTIVE && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    closePost({
                      post_id: initialPostData.contract_post_id || "",
                    });
                    setSelectedPost(null);
                  }}
                >
                  Hold Post
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={postLocked}
                onClick={() => handleRemovePost()}
                loading={isSubmiting}
              >
                Remove
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelectedPost(null)}
                loading={isSubmiting}
              >
                Close
              </Button>
            </Stack>
          </DialogActions>

          {alertData && <Alert alertData={alertData} />}
        </Dialog>
      );
    },
  ),
);

export default ManagePost;
